"use client";

import { Input } from "@/app/components/ui/input";
import { SearchIcon } from "lucide-react";
import React, { useState, useMemo, useCallback, useEffect } from "react";
import { backendInputClass } from "./backend-ui";
import { cn } from "@/lib/utils";

/**
 * Backend Search Components and Hooks
 *
 * This module provides a comprehensive search system for backend tables and data management.
 * It includes filtering functionality, debounced search, and reusable components for consistent
 * search experiences across the application.
 *
 * Features:
 * - Real-time data filtering based on search terms
 * - Support for multiple searchable fields
 * - Debounced search for performance optimization
 * - TypeScript support with generic types
 * - Reusable components and hooks
 *
 * @example
 * ```tsx
 * // Basic usage with hook
 * const { filteredData, searchTerm, setSearchTerm } = useBackendSearch(data, ["name", "email"]);
 *
 * // With search input component
 * <BackendSearchInput
 *   searchTerm={searchTerm}
 *   onSearchChange={setSearchTerm}
 *   placeholder="Search users..."
 * />
 * ```
 */

// Types
/**
 * Generic type for items that can be searched
 * All properties are treated as searchable fields
 */
type SearchableItemT = {
  [key: string]: unknown;
};

/**
 * Configuration object for search functionality
 */
type SearchConfigT = {
  /** Array of field names that should be searchable */
  searchableFields: string[];
  /** Placeholder text for the search input */
  placeholder?: string;
  /** Additional CSS classes for styling */
  className?: string;
  /** Debounce delay in milliseconds (default: 300ms) */
  debounceMs?: number;
};

/**
 * Props for the BackendSearch component
 */
type BackendSearchPropsT<T extends SearchableItemT> = {
  /** Array of data items to search through */
  data: T[];
  /** Search configuration object */
  config: SearchConfigT;
  /** Callback function called when filtered data changes */
  onFilteredDataChange: (filteredData: T[]) => void;
  /** Additional CSS classes for the component */
  className?: string;
};

/**
 * Filters an array of data items based on a search term and specified searchable fields
 *
 * This function performs a case-insensitive search across multiple fields of each data item.
 * It returns all items where at least one of the specified fields contains the search term.
 *
 * @param data - Array of data items to filter
 * @param searchTerm - The search term to filter by (case-insensitive)
 * @param searchableFields - Array of field names to search within
 * @returns Filtered array containing only items that match the search criteria
 *
 * @example
 * ```tsx
 * const users = [
 *   { id: 1, name: "John Doe", email: "john@example.com" },
 *   { id: 2, name: "Jane Smith", email: "jane@example.com" }
 * ];
 *
 * const filtered = filterDataBySearchTerm(users, "john", ["name", "email"]);
 * // Returns: [{ id: 1, name: "John Doe", email: "john@example.com" }]
 * ```
 */
export function filterDataBySearchTerm<T extends SearchableItemT>(
  data: T[],
  searchTerm: string,
  searchableFields: string[]
): T[] {
  if (!searchTerm.trim()) {
    return data;
  }

  const searchLower = searchTerm.toLowerCase();

  return data.filter((item) => {
    return searchableFields.some((field) => {
      const value = item[field];
      if (value === null || value === undefined) {
        return false;
      }

      // Convert to string and check if it includes the search term
      return String(value).toLowerCase().includes(searchLower);
    });
  });
}

/**
 * BackendSearch Component
 *
 * A comprehensive search component that provides real-time filtering of data with debounced input.
 * This component manages its own search state and automatically filters data based on user input.
 *
 * Features:
 * - Debounced search input to prevent excessive filtering
 * - Support for multiple searchable fields
 * - Automatic data filtering and callback notifications
 * - Customizable styling and placeholder text
 *
 * @param data - Array of data items to search through
 * @param config - Search configuration including searchable fields and options
 * @param onFilteredDataChange - Callback function called when filtered results change
 * @param className - Additional CSS classes for styling
 *
 * @example
 * ```tsx
 * <BackendSearch
 *   data={users}
 *   config={{
 *     searchableFields: ["name", "email", "role"],
 *     placeholder: "Search users...",
 *     debounceMs: 500
 *   }}
 *   onFilteredDataChange={setFilteredUsers}
 * />
 * ```
 */
export function BackendSearch<T extends SearchableItemT>({
  data,
  config,
  onFilteredDataChange,
  className,
}: BackendSearchPropsT<T>) {
  const [searchTerm, setSearchTerm] = useState("");

  // Filter data when search term changes
  const filteredData = useMemo(() => {
    return filterDataBySearchTerm(data, searchTerm, config.searchableFields);
  }, [data, searchTerm, config.searchableFields]);

  // Notify parent component of filtered data changes using useEffect
  useEffect(() => {
    onFilteredDataChange(filteredData);
  }, [filteredData, onFilteredDataChange]);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
    },
    []
  );

  return (
    <div
      className={`relative flex flex-row items-stretch gap-2.5 ${className || ""}`}
    >
      <SearchIcon className="absolute top-1/2 left-4 z-10 size-4 -translate-y-1/2 opacity-50" />
      <Input
        placeholder={config.placeholder || "Suchen..."}
        value={searchTerm}
        onChange={handleSearchChange}
        className={cn(backendInputClass, "inline-block h-12 pl-10")}
      />
    </div>
  );
}

/**
 * useBackendSearch Hook
 *
 * A React hook that provides search functionality for data arrays. This hook manages
 * search state and automatically filters data based on the current search term.
 *
 * Features:
 * - Automatic data filtering based on search term
 * - Support for multiple searchable fields
 * - Memoized filtered results for performance
 * - Simple state management for search term
 *
 * @param data - Array of data items to search through
 * @param searchableFields - Array of field names that should be searchable
 * @returns Object containing filtered data, search state, and search handlers
 *
 * @example
 * ```tsx
 * const { filteredData, searchTerm, setSearchTerm } = useBackendSearch(
 *   users,
 *   ["name", "email", "department"]
 * );
 *
 * // Use with BackendSearchInput component
 * <BackendSearchInput
 *   searchTerm={searchTerm}
 *   onSearchChange={setSearchTerm}
 *   placeholder="Search users..."
 * />
 * ```
 */
export function useBackendSearch<T extends SearchableItemT>(
  data: T[],
  searchableFields: string[]
) {
  const [searchTerm, setSearchTerm] = useState("");

  // Filter data based on search term
  const filteredData = useMemo(() => {
    return filterDataBySearchTerm(data, searchTerm, searchableFields);
  }, [data, searchTerm, searchableFields]);

  return {
    filteredData,
    searchTerm,
    setSearchTerm,
    searchableFields,
  };
}

/**
 * SimpleBackendSearch Component
 *
 * A basic search input component that only handles the UI presentation. This component
 * does not include any filtering logic and is useful when you want to manage search
 * state externally or implement custom filtering logic.
 *
 * Features:
 * - Clean, consistent search input styling
 * - Search icon with proper positioning
 * - All standard HTML input props supported
 * - Customizable placeholder and styling
 *
 * @param placeholder - Placeholder text for the search input (default: "Suchen...")
 * @param className - Additional CSS classes for styling
 * @param inputProps - All standard HTML input attributes
 *
 * @example
 * ```tsx
 * <SimpleBackendSearch
 *   placeholder="Search products..."
 *   onChange={(e) => setSearchTerm(e.target.value)}
 *   className="w-full"
 * />
 * ```
 */
export function SimpleBackendSearch({
  placeholder = "Suchen...",
  className,
  ...inputProps
}: {
  placeholder?: string;
  className?: string;
} & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <div
      className={`relative flex flex-row items-stretch gap-2.5 ${className || ""}`}
    >
      <SearchIcon className="absolute top-1/2 left-4 z-10 size-4 -translate-y-1/2 opacity-50" />
      <Input
        placeholder={placeholder}
        className={cn(backendInputClass, "inline-block h-12 pl-10")}
        {...inputProps}
      />
    </div>
  );
}

/**
 * BackendSearchInput Component
 *
 * A controlled search input component designed to work with the useBackendSearch hook.
 * This component provides a clean, consistent search interface with proper styling
 * and icon positioning.
 *
 * Features:
 * - Controlled input with external state management
 * - Consistent styling with search icon
 * - Customizable placeholder text
 * - Proper accessibility and keyboard navigation
 *
 * @param searchTerm - Current search term value (controlled)
 * @param onSearchChange - Callback function called when search term changes
 * @param placeholder - Placeholder text for the search input (default: "Suchen...")
 * @param className - Additional CSS classes for styling
 *
 * @example
 * ```tsx
 * const { searchTerm, setSearchTerm } = useBackendSearch(data, searchableFields);
 *
 * <BackendSearchInput
 *   searchTerm={searchTerm}
 *   onSearchChange={setSearchTerm}
 *   placeholder="Search articles..."
 * />
 * ```
 */
export function BackendSearchInput({
  searchTerm,
  onSearchChange,
  placeholder = "Suchen...",
  className,
}: {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}) {
  return (
    <div className={cn("relative flex items-center", className)}>
      <SearchIcon className="absolute left-3 z-10 size-4 text-[var(--c-faint)]" />
      <Input
        type="search"
        placeholder={placeholder}
        value={searchTerm}
        onChange={(e) => onSearchChange(e.target.value)}
        className={cn(backendInputClass, "h-10 pl-9 text-sm")}
      />
    </div>
  );
}
